import { useEffect, useMemo, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import ClipLoader from "react-spinners/ClipLoader";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  selectLoginStatusReportsComponent,
  getFilteredReport,
  selectActiveDashboardComponent,
  selectCurrentFilteredDashboard,
  selectFilteredReport,
  selectCurrentPageReports,
  selectNumPagesReports,
  selectEffectiveColumns,
  changePageState,
  changeActiveComponent,
  selectNameClient,
  selectListClients,
  changePageSize,
  selectPageSizeCoreId,
  changeSort,
  selectSortBy,
  selectSortOrder,
  toggleColumn,
  updateColumnVisibility,
} from "../DashboardSlice";
import "./../DashboardStyles.scss";
import PaginationItem from "../../../../helpers/PaginationItem";
import { usePagination } from "pagination-react-js";
import { useNavigate } from "react-router-dom";
import { changeCurrentProduct } from "../../../common/commonSlice";
import { formatReportCell } from "../utils/reportFormatters";

interface ReportPaginationProps {
  currentPage: number;
  pageSize: number | null;
  numPages: number;
  sortedDataLength: number;
}

const ReportPagination = ({
  currentPage,
  pageSize,
  numPages,
  sortedDataLength,
}: ReportPaginationProps) => {
  const dispatch = useAppDispatch();
  const { pageNumbers, setActivePage, setRecordsPerPage } = usePagination({
    activePage: currentPage + 1,
    recordsPerPage: pageSize || 30,
    totalRecordsLength: numPages * (pageSize || 30),
    offset: 1,
    navCustomPageSteps: { prev: 5, next: 5 },
    permanentFirstNumber: true,
    permanentLastNumber: true,
  });

  function updateActivePage(pageNumber: number | false) {
    pageNumber && dispatch(changePageState(pageNumber - 1));
    pageNumber && setActivePage(pageNumber);
  }

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div
        style={{
          marginLeft: "3%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <select
          id="report-page-size"
          style={{
            width: "50px",
            height: "30px",
            background: "#0a2a39",
            color: "white",
            border: "none",
            borderRadius: "8px",
            marginRight: "5px",
          }}
          value={pageSize || 30}
          onChange={(e) => {
            dispatch(changePageSize(parseInt(e.target.value)));
            setRecordsPerPage(parseInt(e.target.value));
          }}
        >
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="30">30</option>
          <option value="50">50</option>
        </select>
        <label
          htmlFor="report-page-size"
          style={{ fontSize: "13px", color: "white", margin: "0 0 0 3px" }}
        >
          Registros por página
        </label>
      </div>
      <p
        style={{
          fontSize: "12px",
          color: "rgba(255,255,255,0.6)",
          margin: 0,
        }}
      >
        Mostrando{" "}
        {sortedDataLength > 0
          ? `${currentPage * (pageSize || 10) + 1}–${
              currentPage * (pageSize || 10) + sortedDataLength
            } de ~${numPages * (pageSize || 10)}`
          : "0"}{" "}
        resultados
      </p>
      <nav role="navigation" aria-label="Paginación">
        <ul className="pagination">
          <PaginationItem
            label={`Goto first page ${pageNumbers.firstPage}`}
            onClick={() => updateActivePage(pageNumbers.firstPage)}
          >
            <p style={{ fontSize: "25px", margin: "0px" }}>&laquo;</p>
          </PaginationItem>

          <PaginationItem
            label={`Goto previous page ${pageNumbers.previousPage}`}
            onClick={() => updateActivePage(pageNumbers.previousPage)}
          >
            <p style={{ fontSize: "25px", margin: "0px" }}>&lsaquo;</p>
          </PaginationItem>

          <PaginationItem
            label={`Goto first page ${pageNumbers.firstPage}`}
            active={pageNumbers.firstPage === pageNumbers.activePage}
            onClick={() => updateActivePage(pageNumbers.firstPage)}
          >
            {pageNumbers.firstPage}
          </PaginationItem>

          {pageNumbers.customPreviousPage && (
            <PaginationItem
              label={`Goto page ${pageNumbers.customPreviousPage}`}
              onClick={() => updateActivePage(pageNumbers.customPreviousPage)}
            >
              &middot;&middot;&middot;
            </PaginationItem>
          )}

          {pageNumbers.navigation.map((navigationNumber) => {
            const isFirstOrLastPage =
              navigationNumber === pageNumbers.firstPage ||
              navigationNumber === pageNumbers.lastPage;

            return isFirstOrLastPage ? null : (
              <PaginationItem
                label={`Goto page ${navigationNumber}`}
                key={navigationNumber}
                active={navigationNumber === pageNumbers.activePage}
                onClick={() => updateActivePage(navigationNumber)}
              >
                {navigationNumber}
              </PaginationItem>
            );
          })}

          {pageNumbers.customNextPage && (
            <PaginationItem
              label={`Goto page ${pageNumbers.customNextPage}`}
              onClick={() => updateActivePage(pageNumbers.customNextPage)}
            >
              &middot;&middot;&middot;
            </PaginationItem>
          )}

          {pageNumbers.firstPage !== pageNumbers.lastPage && (
            <PaginationItem
              label={`Goto last page ${pageNumbers.lastPage}`}
              active={pageNumbers.lastPage === pageNumbers.activePage}
              onClick={() => updateActivePage(pageNumbers.lastPage)}
            >
              {pageNumbers.lastPage}
            </PaginationItem>
          )}

          <PaginationItem
            label={`Goto next page ${pageNumbers.nextPage}`}
            onClick={() => updateActivePage(pageNumbers.nextPage)}
          >
            <p style={{ fontSize: "25px", margin: "0px" }}>&rsaquo;</p>
          </PaginationItem>

          <PaginationItem
            label={`Goto last page ${pageNumbers.lastPage}`}
            onClick={() => updateActivePage(pageNumbers.lastPage)}
          >
            <p style={{ fontSize: "25px", margin: "0px" }}>&raquo;</p>
          </PaginationItem>
        </ul>
      </nav>
      <div style={{ height: "10px", width: "10px" }}></div>
    </div>
  );
};

export const Reports = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = useAppSelector(selectLoginStatusReportsComponent);
  const activeComponent = useAppSelector(selectActiveDashboardComponent);
  const currentFilteredRequest = useAppSelector(selectCurrentFilteredDashboard);
  const pageSize = useAppSelector(selectPageSizeCoreId);
  const reports = useAppSelector(selectFilteredReport);
  const columnsReport = useAppSelector(selectEffectiveColumns);
  const currentPage = useAppSelector(selectCurrentPageReports);
  const numPages = useAppSelector(selectNumPagesReports);
  const nameClient = useAppSelector(selectNameClient);
  const listClients = useAppSelector(selectListClients);
  const sortBy = useAppSelector(selectSortBy);
  const sortOrder = useAppSelector(selectSortOrder);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // Cerrar el dropdown de columnas al clickear fuera
  useEffect(() => {
    if (!showColumnPicker) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-column-picker]")) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showColumnPicker]);

  const handleSort = (field: string) => {
    const newOrder: "asc" | "desc" =
      sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    dispatch(changeSort({ sortBy: field, sortOrder: newOrder }));
  };

  // Solo las celdas de id navegan al detalle de su servicio.
  const goToCoreId = (id: string | number) => {
    dispatch(changeCurrentProduct("Core ID"));
    navigate(`/services/coreId/registrations/${id}`);
  };

  const goToHyperFlow = (id: string | number) => {
    dispatch(changeCurrentProduct("HyperFlow"));
    navigate(`/services/hyperFlow/flows/${id}`);
  };

  const activeColumnCount =
    columnsReport?.filter((col) => col.active).length ?? 0;

  useEffect(() => {
    dispatch(
      getFilteredReport({
        filter: currentFilteredRequest,
      })
    );
  }, [currentFilteredRequest]);

  useEffect(() => {
    if (activeComponent != "Reports") {
      dispatch(changeActiveComponent("Reports"));
    }
  }, []);


  const sortedReportData = useMemo(() => {
    const data = reports?.reportData;
    if (!data || data.length === 0) return [];
    if (!sortBy) return [...data]; // copia fresca aunque no haya sort
    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, any>)[sortBy];
      const bVal = (b as Record<string, any>)[sortBy];

      // Comparación numérica si ambos valores son números
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Comparación de strings (case-insensitive)
      const aStr = String(aVal ?? "");
      const bStr = String(bVal ?? "");
      const cmp = aStr.localeCompare(bStr, undefined, { sensitivity: "base" });
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }, [reports?.reportData, sortBy, sortOrder]);

  return (
    <LoadingOverlay
      active={loading === "pending"}
      spinner={<ClipLoader color="white" size="56px" />}
      styles={{
        wrapper: () => ({
          position: "relative",
          height: "100%",
          minHeight: 0,
        }),
      }}
    >
      {reports && sortedReportData.length > 0 ? (
        <div
          className="flex-col"
          style={{ width: "100%", height: "100%", minHeight: 0 }}
        >
          <div className="report-toolbar">
            <h2 className="report-title text-bold primary-color">
              Registros realizados {nameClient && `por: ${nameClient}`}
            </h2>
            <div className="report-toolbar-actions">
              <span className="report-sort-hint">
                El orden aplica a esta página
              </span>
              <div style={{ position: "relative" }} data-column-picker>
                <button
                  className="report-columns-btn"
                  onClick={() => setShowColumnPicker(!showColumnPicker)}
                  aria-haspopup="true"
                  aria-expanded={showColumnPicker}
                >
                  Columnas · {activeColumnCount} ▾
                </button>
                {showColumnPicker && (
                  <div className="report-columns-menu" role="menu">
                    <div className="report-columns-menu-header">
                      <span>Columnas visibles</span>
                    </div>
                    {columnsReport?.map((col) => (
                      <label key={col.field} className="report-columns-option">
                        <input
                          type="checkbox"
                          checked={col.active}
                          onChange={() => {
                            const nextActive = !col.active;
                            // Feedback instantáneo; el server reconcilia al responder.
                            dispatch(toggleColumn(col.field));
                            dispatch(
                              updateColumnVisibility([
                                { field: col.field, active: nextActive },
                              ])
                            );
                          }}
                        />
                        {col.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="report-table-wrapper">
            <table className="table-report" key={`${currentPage}-${pageSize}-${sortedReportData[0]?._id ?? "empty"}`}>
              <thead>
                <tr>
                  {columnsReport?.map(
                    (column) =>
                      column.active && (
                        <th
                          key={column.field}
                          role="columnheader"
                          tabIndex={0}
                          aria-sort={
                            sortBy === column.field
                              ? sortOrder === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                          title={`Ordenar por ${column.name}`}
                          onClick={() => handleSort(column.field)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleSort(column.field);
                            }
                          }}
                        >
                          {column.name}
                          <span className="sort-indicator">
                            {sortBy === column.field
                              ? sortOrder === "asc"
                                ? "▲"
                                : "▼"
                              : ""}
                          </span>
                        </th>
                      )
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedReportData.map((reg) => {
                  const row = reg as unknown as Record<string, any>;
                  return (
                    <tr key={reg._id} className="list-hover">
                      {columnsReport?.map((column) => {
                        if (!column.active) return null;

                        const isCoreId = column.field === "_id";
                        const isHyperFlow = column.field === "FlowId";
                        const goTo =
                          isCoreId && reg._id != null
                            ? () => goToCoreId(reg._id)
                            : isHyperFlow && row.FlowId != null
                            ? () => goToHyperFlow(row.FlowId)
                            : undefined;

                        return (
                          <td
                            key={column.field}
                            className={goTo ? "report-link-cell" : undefined}
                            role={goTo ? "link" : undefined}
                            tabIndex={goTo ? 0 : undefined}
                            onClick={goTo}
                            onKeyDown={
                              goTo
                                ? (e) => {
                                    if (e.key === "Enter") goTo();
                                  }
                                : undefined
                            }
                          >
                            {formatReportCell(column.field, row, {
                              listClients,
                            })}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div
            className="flex-row"
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              flex: "0 0 auto",
              marginTop: "12px",
            }}
          >
            <ReportPagination
              currentPage={currentPage}
              pageSize={pageSize}
              numPages={numPages}
              sortedDataLength={sortedReportData.length}
            />
          </div>
        </div>
      ) : (
        <div className="report-empty">
          {loading === "pending" || loading === "idle" ? null : (
            <>
              <h1 className="text-huge text-bold primary-color">
                No hay registros
              </h1>
              <p className="report-empty-hint">
                Ajusta los filtros para ver resultados.
              </p>
            </>
          )}
        </div>
      )}
    </LoadingOverlay>
  );
};
